/*
 * KeyedSet is a set-like collection for values that carry their own stable key.
 *
 * It is intended for domain objects such as `{ id: number, name: string }`, where
 * the value itself contains the information that identifies it. Callers can add,
 * delete, and iterate values as they would with a Set, while still getting Map-like
 * lookup by the extracted key when they need it.
 *
 * This collection exists to avoid the double bookkeeping required by Map<K, V>.
 * With a Map, callers must repeatedly keep the map key and the value's embedded
 * identity in sync, for example `users.set(user.id, user)`. KeyedSet centralizes
 * that relationship by deriving the key from the value through `getKey`.
 *
 * Identity is therefore based on the extracted key, not object reference equality.
 * Adding another value with the same key replaces the previous value. Likewise,
 * value-based operations such as `has(value)` and `delete(value)` are expected to
 * operate on the extracted key.
 *
 * The extracted key should remain stable while a value is stored in the collection.
 * Mutating the fields used by `getKey` can make the stored Map entry inconsistent
 * with the value's current key.
 */
class KeyedSetImpl {
    innerMap = new Map();
    getKey;
    constructor(getKey) {
        this.getKey = getKey;
    }
    get size() {
        return this.innerMap.size;
    }
    add(value) {
        this.innerMap.set(this.getKey(value), value);
        return this;
    }
    delete(value) {
        return this.innerMap.delete(this.getKey(value));
    }
    clear() {
        this.innerMap.clear();
    }
    has(value) {
        return this.innerMap.has(this.getKey(value));
    }
    entries() {
        return this.innerMap.entries();
    }
    keys() {
        return this.innerMap.keys();
    }
    values() {
        return this.innerMap.values();
    }
    [Symbol.iterator]() {
        return this.values();
    }
    get [Symbol.toStringTag]() {
        return "KeyedSet";
    }
    hasKey(key) {
        return this.innerMap.has(key);
    }
    getByKey(key) {
        return this.innerMap.get(key);
    }
    deleteByKey(key) {
        return this.innerMap.delete(key);
    }
}
export const KeyedSet = KeyedSetImpl;
//# sourceMappingURL=keyed-set.js.map